import { useState, type ReactNode } from "react";
import {
  Pause,
  Play,
  Power,
  RotateCw,
  Square,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useResourcePowerAction } from "@/hooks/useResourceQueries";
import {
  RESOURCE_POWER_ACTION_META,
  type ResourcePowerAction,
} from "@/lib/resourcePowerActions";
import type { ResourceStatus, ResourceType } from "@/types/admin";

/**
 * The fields a power action needs — satisfied by both `Resource` and `ResourceDetail`.
 *
 * `status` is optional so an older caller that never had it to hand keeps compiling; without it,
 * nothing here is blocked. `id` addresses the endpoint, `name` fills the destructive confirmations,
 * and `type` is the kind check.
 */
export interface PowerActionTarget {
  id: string;
  name: string;
  type: ResourceType;
  status?: ResourceStatus;
}

export interface AvailablePowerAction {
  action: ResourcePowerAction;
  label: string;
  Icon: LucideIcon;
  /** Bypasses the guest OS — render it in the destructive colour and confirm before running. */
  destructive: boolean;
  /** True while this action's own request is in flight — swap its icon for a spinner. */
  isRunning: boolean;
  /**
   * Fires the mutation, or opens the confirmation first for a destructive action.
   *
   * `onSettled` runs once the request finishes. A menu does not use it to stay open: Radix menus
   * are modal, so holding one open for a provider round trip locks the whole console. Menus close
   * on select and show `isPending` on their trigger instead.
   */
  run: (options?: { onSettled?: () => void }) => void;
}

const ICONS: Record<ResourcePowerAction, LucideIcon> = {
  start: Play,
  shutdown: Power,
  stop: Square,
  restart: RotateCw,
  reset: Zap,
  suspend: Pause,
  resume: Play,
};

/**
 * The seven power actions, ready to render on any surface.
 *
 * All seven, always, for any VM whose status isn't blocked below: the admin endpoints run no other
 * status or expiry gate, so beyond that there is nothing left to derive an "available" list from. An
 * action the machine cannot take is refused by the hypervisor and comes back as a toast carrying the
 * provider's own reason — which is what an operator repairing a stuck row needs to see, and strictly
 * more than a hidden menu item told them. The client console keeps its own status-derived gating;
 * this is the operator surface only.
 *
 * The list menu and the detail hero share this hook so the mutation and the confirm dialog live
 * here once and each surface only decides what to render them as (menu items vs buttons). Callers
 * must render `confirmDialog` — without it the destructive actions never fire.
 *
 * Every action here is SuperAdmin-only on the backend; wrap the rendered controls in
 * `PermissionGate roles="SuperAdmin"` so a SupportAdmin is not shown buttons that answer 403.
 *
 * The target may be null while a detail page is still loading, and a non-VM resource has no power
 * state at all — the hook offers nothing in both cases, so callers never have to make the call
 * conditional.
 */
export function useResourcePowerActions(resource?: PowerActionTarget | null): {
  actions: AvailablePowerAction[];
  confirmDialog: ReactNode;
  /** True while any of the seven is in flight — they are mutually exclusive on one VM. */
  isPending: boolean;
  /** The one that is actually running, or null. */
  pendingAction: ResourcePowerAction | null;
  /**
   * Why every action is disabled right now, or null when none of them are. The admin power endpoint
   * refuses `Terminating`/`Decommissioning` with `INVALID_STATE` — the row is mid-deletion or
   * mid-reinstall, both of them their own round trip to the provider that a power action would race.
   * A caller renders the seven items disabled with this as their `title` rather than hiding them,
   * matching how the menu treats every other transient block.
   */
  blockedReason: string | null;
} {
  const powerAction = useResourcePowerAction();
  const [pendingConfirm, setPendingConfirm] =
    useState<ResourcePowerAction | null>(null);

  // The one check that survives, and it is about the kind of resource rather than its state: the
  // backend answers UNSUPPORTED_RESOURCE_TYPE for a disk, network or bucket, so those surfaces get
  // no buttons at all.
  const isVm = resource?.type === "VirtualMachine";

  const blockedReason =
    resource?.status === "Terminating"
      ? "Being deleted"
      : resource?.status === "Decommissioning"
        ? "Being reinstalled"
        : null;

  const run = (
    action: ResourcePowerAction,
    options?: { onSettled?: () => void },
  ) => {
    if (!resource) return;
    powerAction.mutate({ id: resource.id, action }, options);
  };

  // `variables` outlives the request, so it only identifies the running action while pending.
  const pendingAction = powerAction.isPending
    ? (powerAction.variables?.action ?? null)
    : null;

  // Fixed order: the everyday actions first, the two guest-bypassing ones last, so a mis-click
  // near the top of the menu cannot pull the plug on a VM.
  const order: ResourcePowerAction[] = [
    "start",
    "resume",
    "shutdown",
    "restart",
    "suspend",
    "stop",
    "reset",
  ];

  const actions: AvailablePowerAction[] =
    resource && isVm
      ? order.map((action) => {
          const meta = RESOURCE_POWER_ACTION_META[action];

          return {
            action,
            label: meta.label,
            Icon: ICONS[action],
            destructive: meta.destructive,
            isRunning: pendingAction === action,
            run: (options) =>
              meta.destructive
                ? setPendingConfirm(action)
                : run(action, options),
          };
        })
      : [];

  const confirmMeta = pendingConfirm
    ? RESOURCE_POWER_ACTION_META[pendingConfirm]
    : null;

  const confirmDialog = (
    <ConfirmDialog
      open={pendingConfirm !== null}
      onOpenChange={(open) => !open && setPendingConfirm(null)}
      title={confirmMeta?.confirmTitle ?? ""}
      description={(confirmMeta?.confirmDescription ?? "").replace(
        "{name}",
        resource?.name ?? "",
      )}
      confirmText={confirmMeta?.label ?? "Confirm"}
      variant="destructive"
      isPending={powerAction.isPending}
      onConfirm={() => {
        // The dialog is held open by ConfirmDialog's async mode, so the spinner covers the
        // request and the operator sees it land instead of guessing from a closed dialog.
        if (pendingConfirm) {
          run(pendingConfirm, { onSettled: () => setPendingConfirm(null) });
        }
      }}
    />
  );

  return {
    actions,
    confirmDialog,
    isPending: powerAction.isPending,
    pendingAction,
    blockedReason,
  };
}
